import { v4 as uuidv4, validate as validateUuid } from "uuid";

/**
 * Wire-boundary translation for wizard draft scope fields.
 *
 * `useWizardStepDraft` callers pass a scope identifier that is one of two
 * things:
 *   - a real entity id (a bank UUID) for per-entity drafts like
 *     StepLiftsFloors — this is already a genuine UUID, and
 *   - a step slug ("bacnet", "server", "channels-form", ...) for
 *     whole-step drafts, which have no natural entity to key on.
 *
 * LiftCloud's `wizard_drafts.scope_id` column is a Postgres `uuid`, so a
 * slug can't be sent as-is (it 400s with "invalid scope ID"). Rather than
 * derive a fake UUID from the slug, a whole-step draft is given a *real*
 * UUID that is minted once — the first time that (org, step, slug) draft
 * is touched — and then reused from then on. The mapping lives in
 * localStorage so the same UUID is used across page reloads, browser
 * crashes, and reboots on this machine, and the draft always round-trips
 * to the same server row. Per-entity drafts pass their real UUID through
 * untouched.
 *
 * `scope_type` is constrained server-side to `oneof=bank server org`.
 * Whole-step drafts are scoped to the organization's wizard flow (one per
 * org per step), so `"step"` maps to `"org"`; per-entity scope types
 * (`"bank"`) pass through.
 */

const STORAGE_PREFIX = "wizardDraftScopeId";

// In-memory fallback for environments where localStorage is unavailable
// or throws (private mode, disabled storage, non-browser test runners).
// Keeps the minted UUID stable at least for the lifetime of the tab.
const memoryScopeIds = new Map<string, string>();

function storageKey(orgId: string, step: number, slug: string): string {
  return `${STORAGE_PREFIX}:${orgId}:${step}:${slug}`;
}

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return memoryScopeIds.get(key) ?? null;
  }
}

function writeStored(key: string, value: string): void {
  memoryScopeIds.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable — the in-memory map above still keeps the UUID
    // stable within this tab.
  }
}

/**
 * Resolve the real UUID to send as `scope_id` for a wizard draft.
 *
 * A caller-supplied real UUID (per-entity draft) is returned verbatim.
 * A slug (whole-step draft) is mapped to a persisted, minted UUID: the
 * first call for a given (org, step, slug) generates a fresh v4 UUID and
 * remembers it; every later call returns the same one.
 */
export function toWireScopeId(orgId: string, step: number, scopeId: string): string {
  if (validateUuid(scopeId)) return scopeId;

  const key = storageKey(orgId, step, scopeId);
  const existing = readStored(key);
  if (existing && validateUuid(existing)) return existing;

  const minted = uuidv4();
  writeStored(key, minted);
  return minted;
}

export function toWireScopeType(scopeType: string): string {
  return scopeType === "step" ? "org" : scopeType;
}
